import { Link, useLocation } from 'react-router-dom';
import { Icon } from '@phosphor-icons/react';

interface NavItem {
  path: string;
  icon: Icon;
  label: string;
  badge?: string;
  permission: string;
}

interface Alerts {
  unreadMessages: number;
  urgentMessages: number;
  activeIssues: number;
  urgentIssues: number;
}

interface NavLinkProps {
  item: NavItem;
  mobile?: boolean;
  nested?: boolean;
  alerts: Alerts;
  onMobileClose?: () => void;
  onFetchAlerts?: () => void;
}

export function NavLink({ 
  item, 
  mobile = false, 
  nested = false, 
  alerts,
  onMobileClose,
  onFetchAlerts
}: NavLinkProps) {
  const location = useLocation();
  const isActive = location.pathname === item.path;
  const IconComponent = item.icon;
  
  // Determine badge count and urgent status based on badge type
  let badgeCount = 0;
  let isUrgent = false;
  
  if (item.badge === 'messages') {
    badgeCount = alerts.unreadMessages;
    isUrgent = alerts.urgentMessages > 0;
  } else if (item.badge === 'issues') {
    badgeCount = alerts.activeIssues;
    isUrgent = alerts.urgentIssues > 0;
  }
  
  return (
    <Link
      to={item.path}
      onClick={() => {
        if (mobile && onMobileClose) onMobileClose();
        if (item.badge && onFetchAlerts) onFetchAlerts();
      }}
      className={`${isActive ? 'nav-item-active' : 'nav-item'} ${mobile ? 'text-base' : ''} ${nested ? 'pl-9 text-sm' : ''}`}
      data-testid={`nav-${item.path.slice(1)}`}
    >
      <span className="relative">
        <IconComponent size={nested ? 16 : 20} weight={isActive ? 'fill' : 'regular'} />
        {badgeCount > 0 && (
          <span 
            className={`absolute -top-2 -right-2 min-w-[16px] h-[16px] flex items-center justify-center text-[10px] font-semibold rounded-full px-0.5 border border-slate-900 ${
              isUrgent 
                ? 'text-white' 
                : 'bg-white text-slate-900'
            }`}
            style={isUrgent ? {
              animation: 'urgentPulse 1.5s ease-in-out infinite'
            } : {}}
          >
            {badgeCount > 99 ? '99+' : badgeCount}
          </span>
        )}
      </span>
      <span>{item.label}</span>
    </Link>
  );
}
